/* eslint-disable no-param-reassign */
import * as Sentry from '@sentry/browser';
import { FAILURE_MESSAGE_BY_EVENT, SIGNUP_EVENTS } from '../utils/common-literals';
import { isUKBrand, isCanadaBrand, isLexReceptionUKBrand } from './brands';
import getSessionInfo from './session-Info';

const { staticPlansAndAddons } = getSessionInfo();

export function getQueryParam(paramName) {
  const locationObj = new URL(window.location.href);
  const params = new URLSearchParams(locationObj.search);
  return params.get(paramName);
}

export function getUserSignupSource() {
  const utmSource = getQueryParam('utm_source');
  return utmSource ? decodeURIComponent(utmSource) : 'Direct';
}

export function getParamsFromResponse(response = {}) {
  const {
    status = false, userId, planId, quantity,
  } = response;
  let param = `success=${status}`;
  if (userId) {
    param = `${param}&userId=${userId}`;
  }
  if (planId) {
    param = `${param}&planId=${planId}`;
  }
  if (quantity) {
    param = `${param}&quantity=${quantity}`;
  }
  return param;
}

export function getStateFromResponse(response = {}) {
  const {
    status = false, userId, planId, freeTrialId, quantity,
  } = response;

  const state = {};
  state.action = 'signup';
  state.success = status;

  if (userId) {
    state.userId = userId;
  }
  if (planId) {
    state.planId = planId;
  }
  if (freeTrialId) {
    state.freeTrialId = freeTrialId;
  }
  if (quantity) {
    state.quantity = quantity;
  }

  return encodeURIComponent(btoa(JSON.stringify(state)));
}

export function getPaymentRedirectUrl(response) {
  const url = response?.dashboardUrl || document.referrer;
  return url && `${url}${url.includes('?') ? '&' : '?'}${getParamsFromResponse(response)}`;
}

export function getSignupRedirectUrl(response) {
  const url = response?.redirectUrl || document.referrer;

  /* continueUrl should be completely encoded when passing
      it to fullauth to avoid param dropping on redirect. */
  if (url && url.includes('?continue=')) return `${url}${encodeURIComponent('?state=')}${getStateFromResponse(response)}`;

  // this logic is specific for wordpress signup redirect
  if (url?.includes('companyKey')) {
    return url;
  }

  return url && `${url}?state=${getStateFromResponse(response)}`;
}

export function redirect(url) {
  if (url) {
    window.location.replace(url);
  }
}

export function reducerFactory(initialState, handlers) {
  return function reducer(state = initialState, action) {
    const handler = handlers[action.type];

    if (handler) {
      return handler(state, action);
    }

    return state;
  };
}

export function getFirstAndLastNameFromFullName(fullName = '') {
  if (!fullName) return { firstName: '', lastName: '' };
  const nameArray = fullName.trim().split(' ');
  const [firstName] = nameArray;
  const lastName = nameArray.length > 1 ? nameArray.splice(1).join(' ') : '';
  return { firstName, lastName };
}

export function getFullName({ firstName, lastName } = {}) {
  if (firstName && lastName) return `${firstName} ${lastName}`;
  if (firstName) return firstName;
  return '';
}

export function cleanPhone(phone = '') {
  return phone.replace(/[^\d]/g, ''); // eslint-disable-line
}

export function addDaysToCurrentDate(days = 0) {
  const date = new Date();
  if (days) {
    date.setDate(date.getDate() + days);
  }
  return date;
}

export function getFormattedDate({
  options = {
    year: 'numeric', month: 'long', day: 'numeric',
  }, language = 'en-IN', date = new Date(),
} = {}) {
  return new Intl.DateTimeFormat(language, options).format(date);
}

export function addMonthsToCurrentMonth(months = 0) {
  const date = new Date();
  if (months) {
    date.setMonth(date.getMonth() + months);
  }
  return date;
}

export function getFormattedYear(options) {
  return getFormattedDate(options).split(' ')[2];
}

export function isAchEnabled() {
  return getQueryParam('ach') === 'true';
}

export function getUniqueUserIdFromQueryParam() {
  return (
    getQueryParam('leadUniquePin')
    || getQueryParam('dealid')
    || getQueryParam('accountId')
  );
}

export function getPostalCodeDisplayName() {
  if (isCanadaBrand) {
    return 'Postal Code';
  }
  if (isUKBrand) {
    return 'Post Code';
  }
  return 'Zip Code';
}

export function getPostalCodeErrorDisplayName() {
  if (isCanadaBrand) {
    return ['Enter Postal Code', 'Invalid Postal Code'];
  }
  if (isUKBrand) {
    return ['Enter Post Code', 'Invalid Post Code'];
  }
  return ['Enter Zip Code', 'Invalid Zip Code'];
}


export function getUserIdentifierForAnalytics({ email, accountId } = {}) {
  let analyticsMessage = '';
  if (getQueryParam('leadUniquePin')) {
    analyticsMessage += `leadUniquePin:${getQueryParam('leadUniquePin')} `;
  } else if (getQueryParam('dealid')) {
    analyticsMessage += `dealid:${getQueryParam('dealid')} `;
  } else if (accountId) {
    analyticsMessage += `accountId:${accountId} `;
  } else if (getQueryParam('accountId')) {
    analyticsMessage += `accountId:${getQueryParam('accountId')} `;
  }

  analyticsMessage += `emailId:${email || getQueryParam('email')}`;
  return analyticsMessage;
}

export function getSanitizedErrorMessage(
  apiResponse = {},
  onlyPayment = false,
) {
  const flowType = onlyPayment ? 'UPGRADE' : 'SIGNUP';
  let { message = '', analyticsMessage = '' } = apiResponse;
  try {
    if (!message) {
      message = !staticPlansAndAddons && !window.location?.pathname?.includes('/error')
        ? SIGNUP_EVENTS.PLANS_NOT_AVAILABLE
        : SIGNUP_EVENTS.INTERNAL_SERVER_ERROR;
    }

    if (message?.startsWith('Recaptcha')) {
      analyticsMessage = message;
      message = SIGNUP_EVENTS.RECAPTCHA_FAILED;
    } else if (message?.startsWith('UNAUTHORISED_USER_ROLE_')) {
      analyticsMessage = message;
      message = SIGNUP_EVENTS.UNAUTHORISED_UPGRADE_ERROR;
    }

    const errorMessageInfo = FAILURE_MESSAGE_BY_EVENT[flowType][message];

    return {
      errorHeader:
        errorMessageInfo?.header
        || FAILURE_MESSAGE_BY_EVENT[flowType].ERROR_HEADER,
      errorMessage: errorMessageInfo?.message || message,
      analyticsMessage:
        errorMessageInfo?.analyticsMessage
        || analyticsMessage
        || message
        || 'Empty response.',
    };
  } catch (error) {
    const err = new Error(`Error occurred while sanitizing error message: ${error}`);
    err.name = 'SanitizeErrorMessageError';
    err.cause = error;
    Sentry.captureException(err);
    return {
      errorHeader: FAILURE_MESSAGE_BY_EVENT[flowType].ERROR_HEADER,
      errorMessage: message,
      analyticsMessage: analyticsMessage || message || 'Empty response.',
    };
  }
}

export function appendRegionPathPrefix(path) {
  if (isCanadaBrand) {
    return `/ca${path}`;
  }
  if (isLexReceptionUKBrand) {
    return `/uk${path}`;
  }
  return path;
}

export function isLiveEnvironment() {
  return !!process?.env?.PRODUCTION;
}

export const CURRENCY = isUKBrand ? '£' : '$';

export const redirectToErrorPage = () => redirect(appendRegionPathPrefix('/error'));

export function decodeEncodedUrlParam(encodedState) {
  try {
    const decodedState = decodeURIComponent(encodedState);
    const base64Decoded = atob(decodedState);
    return JSON.parse(base64Decoded);
  } catch (error) {
    Sentry.captureException(error, {
      extra: {
        message: 'Error decoding state param',
        encodedState,
      },
    });
    return null;
  }
}

export function formatAccountNumber(number) {
  if (!number) return '';
  return number.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3');
}
